/**
 * 分类下钻抽屉（**K 域 `AC-K2.9` / `AC-K2.10` / `AC-K3.3`**）—— ① 段分类树的明细宿主
 *
 * 依据：K 域 PRD **§4.3**（细节**全部下钻**）· **`AC-K2.9`**（🔴 卡片与抽屉**两套口径不可互代**，
 * UI **须标注差异** · 必测）· **`AC-K2.10`**（下钻空态**必须解释**「该行金额来源为摊销，
 * 无对应流水明细」，**不得空白** · 必测）· 原型 `13-报表_原型.html` 的 `#drCat` / `#drEmpty`。
 *
 * ## 🔴 本抽屉**一行口径都不实现**
 *
 * | 事 | 谁做 |
 * |---|---|
 * | 「按分类 + 窗口取流水」 | 🔴 **C 域同一条流水查询** —— 复用 `buildJournalQuery()` + `listTransactions()`（不自拼 query） |
 * | 行的标题 / 副标题 | C 域 `rowTitle()` / `rowSubtitle()`（**同一实现**，改名不会两处不一致） |
 * | 行的金额 | C 域 `rowAmount()`（**同一实现** —— 这才是"两套口径不可互代"的正确解法：不是各写一份） |
 * | 分页大小 | 服务端默认（**不传 `page`**） |
 *
 * ## 🔴 为什么抽屉里**不显示合计**
 *
 * 「仅流水口径」的合计需要在端侧把多行金额加总 —— 那是**第二套金额口径**（本项目禁端侧 `reduce`
 * 与手写金额运算）。⇒ 抽屉只报**条数**，并把**卡片一侧**的金额写进差异标注（我们有它），
 * 让"两者可以不等"这句话是**可验证的**（而不是空口）。
 */

import { Alert, Box, Divider, Drawer, IconButton, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { useQuery } from '@tanstack/react-query';
import { formatMoneyText, type Cents, type TransactionRowView } from '@jiaying/shared';
import { listTransactions } from '../api/endpoints.js';
import { buildJournalQuery, EMPTY_JOURNAL_FILTER, JournalWindow } from '../features/journal-filter.js';
import { rowAmount, rowSubtitle, rowTitle } from '../features/journal-labels.js';
import {
  AMORTIZED_ONLY_HINT,
  DRILL_CALIBER,
  DRILL_EMPTY_EXPLAIN,
  DRILL_ERROR,
  DRILL_LOADING,
  drillCaliberNote,
  drillCountText,
  drillTitle,
} from '../features/report-labels.js';
import { Amount } from './Amount.js';

export interface DrillTarget {
  id: string;
  name: string;
  /** 1 = 一级（服务端会把它展开成整支）· 2 = 二级 */
  level: 1 | 2;
}

/**
 * 抽屉的**内容**（不含 `Drawer` 外壳）。
 *
 * 🔴 **为什么把内容抽出来**：MUI `Drawer` 会把子树挂到 **Portal**（`document.body`），
 * 而端侧测试基座是 `renderToStaticMarkup`（无 DOM）⇒ **Portal 里的内容根本不在产物里**
 * （铁律 30 的既有结论）。抽出来之后，"文案 / 空态 / 口径标注"这些**内容**才可被断言，
 * 而外壳只剩 `open` 与 `onClose` 两根线（那两根线由 `ReportPage` 的接线承担）。
 */
export function ReportCategoryDrawerBody({
  target,
  window,
  currency,
  cardCents,
  onClose,
}: {
  target: DrillTarget;
  window: { from: string; to: string };
  currency: string;
  cardCents: number | null;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ['report-drill', target.id, window.from, window.to],
    queryFn: () =>
      listTransactions(
        // 🔴 复用 C 域的同一套拼装（`Custom` + 显式 from/to + 分类维度）—— 不手写 query 串
        buildJournalQuery(
          {
            ...EMPTY_JOURNAL_FILTER,
            window: JournalWindow.Custom,
            from: window.from,
            to: window.to,
            categoryIds: [target.id],
          },
          { today: window.to },
        ),
      ),
  });

  const rows: readonly TransactionRowView[] = query.data?.items ?? [];

  return (
    <Box sx={{ width: { xs: '92vw', sm: 460 }, p: 2 }} data-testid="report-drill-drawer">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }} data-testid="drill-title">
          {drillTitle(target.name)}
        </Typography>
        {/* 口径徽标（原型 `.caliber`） */}
        <Typography variant="caption" color="text.secondary" data-testid="drill-caliber">
          {DRILL_CALIBER}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="收起" data-testid="drill-close">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" data-testid="drill-window">
        {window.from} ~ {window.to}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      {query.isPending ? (
        <Typography variant="body2" color="text.secondary" data-testid="drill-loading">
          {DRILL_LOADING}
        </Typography>
      ) : null}
      {query.isError ? (
        <Alert severity="error" data-testid="drill-error">
          {DRILL_ERROR}
        </Alert>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <Alert severity="info" data-testid="drill-empty">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {AMORTIZED_ONLY_HINT}
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            {DRILL_EMPTY_EXPLAIN}
          </Typography>
        </Alert>
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary" data-testid="drill-count">
            {drillCountText(rows.length)}
          </Typography>
          <List dense disablePadding>
            {rows.map((row) => {
              const a = rowAmount(row);
              return (
                <ListItem
                  key={row.id}
                  disableGutters
                  data-testid={`drill-row-${row.id}`}
                  secondaryAction={
                    <Amount
                      cents={a.cents}
                      currency={currency}
                      colored={a.colored}
                      signed={a.signed}
                      variant="body2"
                    />
                  }
                >
                  <ListItemText primary={rowTitle(row)} secondary={rowSubtitle(row)} />
                </ListItem>
              );
            })}
          </List>
        </>
      ) : null}

      {/* 🔴 两套口径的差异标注（`AC-K2.9` 必测）—— 抽屉与卡片**永远写在同一屏** */}
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" component="div" data-testid="drill-caliber-note">
        {cardCents === null
          ? '卡片金额含待摊摊销，本抽屉仅列流水明细 ⇒ 两者可以不等，这是设计如此。'
          : drillCaliberNote(formatMoneyText(cardCents as Cents, currency))}
      </Typography>
    </Box>
  );
}

export function ReportCategoryDrawer({
  target,
  window,
  currency,
  cardCents,
  onClose,
}: {
  /** `null` ⇒ 抽屉关闭 */
  target: DrillTarget | null;
  /** ① 段的窗口（`ReportWindowView`）—— 下钻**必须用同一窗口**，否则两处口径不可比 */
  window: { from: string; to: string };
  currency: string;
  /** 卡片上该分类的金额（口径对照用；`null` = 拿不到就不提具体数） */
  cardCents: number | null;
  onClose: () => void;
}) {
  return (
    <Drawer anchor="right" open={target !== null} onClose={onClose}>
      {target === null ? null : (
        <ReportCategoryDrawerBody
          target={target}
          window={window}
          currency={currency}
          cardCents={cardCents}
          onClose={onClose}
        />
      )}
    </Drawer>
  );
}
